import React from 'react';
import { Check } from 'lucide-react';

const ProfileSetupModal = ({
  show,
  onClose,
  currentUser,
  emailVerified,
  profileStep,
  setProfileStep,
  profileData,
  setProfileData,
  handleSaveProfile,
  handleSendVerification,
  handleCheckVerification
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="rock-background gig-card border-2 border-electric max-w-3xl w-full my-8">            
        <div className="mb-6">
          <h2 className="concert-heading text-4xl text-electric mb-2">
            🎸 COMPLETE YOUR ARTIST PROFILE
          </h2>
          <p className="text-gray-light">
            Fill out your profile to start creating gigs and going live!
          </p>
          
          {/* Progress Indicator */}
          <div className="mt-4 flex items-center gap-2">
            <div className={`flex-1 h-2 rounded-full ${profileStep >= 1 ? 'bg-electric' : 'bg-white/20'}`}></div>
            <div className={`flex-1 h-2 rounded-full ${profileStep >= 2 ? 'bg-electric' : 'bg-white/20'}`}></div>
            <div className={`flex-1 h-2 rounded-full ${profileStep >= 3 ? 'bg-electric' : 'bg-white/20'}`}></div>
          </div>
          <p className="text-electric text-sm mt-2 font-bold">Step {profileStep} of 3</p>
        </div>

        {/* Email Verification Warning */}
        {!emailVerified && (
          <div className="bg-red-500/20 border-2 border-red-400 rounded-lg p-6 mb-6">
            <p className="text-red-300 font-bold text-lg mb-2">⚠️ EMAIL NOT VERIFIED</p>
            <p className="text-white mb-4">
              You must verify your email before completing your profile.
            </p>
            <p className="text-red-200 text-sm mb-4">
              Check your inbox: <span className="font-bold">{currentUser?.email}</span>
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleSendVerification}
                className="flex-1 btn btn-neon text-sm"
              >
                📧 Send Verification Email
              </button>
              <button
                onClick={handleCheckVerification}
                className="flex-1 btn btn-electric text-sm"
              >
                🔄 Check Status
              </button>
            </div>
          </div>
        )}

        {/* Privacy Message */}
        <div className="bg-green-500/20 border border-neon rounded-lg p-4 mb-6">
          <p className="text-neon font-bold text-sm">
            🔒 Your email ({currentUser?.email}) will NOT be displayed publicly and remains confidential.
          </p>
        </div>

        <div className="space-y-6">
          {/* Step 1: Basic Info */}
          {profileStep === 1 && (
            <div className="space-y-4">
              <h3 className="concert-heading text-2xl text-magenta mb-4">📝 BASIC INFORMATION</h3>
              
              <div>
                <label className="text-electric font-bold mb-2 block text-sm">
                  Artist/Band Name *
                </label>
                <input
                  type="text"
                  value={profileData.artistName}
                  onChange={(e) => setProfileData({...profileData, artistName: e.target.value})}
                  placeholder="e.g. The Electric Waves"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none"
                />
              </div>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm">
                  Your Full Name *
                </label>
                <input
                  type="text"
                  value={profileData.fullName}
                  onChange={(e) => setProfileData({...profileData, fullName: e.target.value})}
                  placeholder="e.g. John Smith"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none"
                />
              </div>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm">
                  Bio * (minimum 50 characters)
                </label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                  placeholder="Tell your audience about your music, style, and story..."
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none"
                />
                <p className={`text-sm mt-1 ${profileData.bio.length >= 50 ? 'text-neon' : 'text-gray'}`}>
                  {profileData.bio.length}/50 characters
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-electric font-bold mb-2 block text-sm">
                    Genre/Style *
                  </label>
                  <input
                    type="text"
                    value={profileData.genre}
                    onChange={(e) => setProfileData({...profileData, genre: e.target.value})}
                    placeholder="e.g. Rock, Jazz, Electronic"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-electric font-bold mb-2 block text-sm">
                    Location/City *
                  </label>
                  <input
                    type="text"
                    value={profileData.location}
                    onChange={(e) => setProfileData({...profileData, location: e.target.value})}
                    placeholder="e.g. New York, NY"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none"
                  />
                </div>
              </div>

              <button
                onClick={() => {
                  if (!profileData.artistName || !profileData.fullName || !profileData.bio || 
                      profileData.bio.length < 50 || !profileData.genre || !profileData.location) {
                    alert('⚠️ Please fill in all required fields!');
                    return;
                  }
                  setProfileStep(2);
                }}
                className="btn btn-neon w-full text-lg"
              >
                Next: Social Media →
              </button>
            </div>
          )}

          {/* Step 2: Social Media */}
          {profileStep === 2 && (
            <div className="space-y-4">
              <h3 className="concert-heading text-2xl text-magenta mb-4">🔗 SOCIAL MEDIA (Optional)</h3>
              <p className="text-gray-light text-sm mb-4">
                Add your social media links so fans can follow you!
              </p>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm flex items-center gap-2">
                  <span>📷</span> Instagram
                </label>
                <input
                  type="text"
                  value={profileData.socialMedia.instagram}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    socialMedia: {...profileData.socialMedia, instagram: e.target.value}
                  })}
                  placeholder="@username or full URL"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none"
                />
              </div>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm flex items-center gap-2">
                  <span>👍</span> Facebook
                </label>
                <input
                  type="text"
                  value={profileData.socialMedia.facebook}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    socialMedia: {...profileData.socialMedia, facebook: e.target.value}
                  })}
                  placeholder="Page URL"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none"
                />
              </div>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm flex items-center gap-2">
                  <span>💼</span> LinkedIn
                </label>
                <input
                  type="text"
                  value={profileData.socialMedia.linkedin || ''}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    socialMedia: {...profileData.socialMedia, linkedin: e.target.value}
                  })}
                  placeholder="https://linkedin.com/in/yourprofile"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none"
                />
              </div>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm flex items-center gap-2">
                  <span>📺</span> YouTube
                </label>
                <input
                  type="text"
                  value={profileData.socialMedia.youtube}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    socialMedia: {...profileData.socialMedia, youtube: e.target.value}
                  })}
                  placeholder="Channel URL"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setProfileStep(1)}
                  className="btn btn-ghost"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setProfileStep(3)}
                  className="flex-1 btn btn-neon text-lg"
                >
                  Next: Review →
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Review & Save */}
          {profileStep === 3 && (
            <div className="space-y-4">
              <h3 className="concert-heading text-2xl text-magenta mb-4">✅ REVIEW & SAVE</h3>

              <div className="bg-white/5 rounded-lg p-6 space-y-3">
                <div>
                  <p className="text-gray text-sm">Artist Name</p>
                  <p className="text-white font-bold text-lg">{profileData.artistName}</p>
                </div>
                <div>
                  <p className="text-gray text-sm">Full Name</p>
                  <p className="text-white font-bold">{profileData.fullName}</p>
                </div>
                <div>
                  <p className="text-gray text-sm">Bio</p>
                  <p className="text-white">{profileData.bio}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray text-sm">Genre</p>
                    <p className="text-electric font-bold">{profileData.genre}</p>
                  </div>
                  <div>
                    <p className="text-gray text-sm">Location</p>
                    <p className="text-electric font-bold">{profileData.location}</p>
                  </div>
                </div>
                
                {Object.values(profileData.socialMedia).some(v => v) && (
                  <div>
                    <p className="text-gray text-sm mb-2">Social Media</p>
                    <div className="flex flex-wrap gap-2">
                      {profileData.socialMedia.instagram && (
                        <span className="px-3 py-1 bg-magenta/20 text-magenta rounded-full text-sm">📷 Instagram</span>
                      )}
                      {profileData.socialMedia.facebook && (
                        <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">👍 Facebook</span>
                      )}
                      {profileData.socialMedia.spotify && (
                        <span className="px-3 py-1 bg-green-500/20 text-neon rounded-full text-sm">🎵 Spotify</span>
                      )}
                      {profileData.socialMedia.youtube && (
                        <span className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm">📺 YouTube</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setProfileStep(2)}
                  className="btn btn-ghost"
                >
                  ← Back
                </button>
                <button
                  onClick={handleSaveProfile}
                  className="flex-1 btn btn-neon text-lg"
                >
                  <Check size={20}/>
                  <span>Save Profile & Continue</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSetupModal;
